import { ReactCountryFlag } from "react-country-flag";
import GenericTable from "../../../../common/components/GenericTable";
import { ColModel } from "../../../../common/components/GenericTableRow";
import CommentIcon from "@mui/icons-material/Comment";
import { primaryColor } from "../../../../core/theme";
import { Tender } from "../../../tender_page/tender.module";
import { IconButton, Typography } from "@mui/material";
import { updateTenders } from "../../../tender_page/tender.thunk";
import { If } from "../../../../common/components/If";
import { useSelector } from "react-redux";
import {
	selectAuthenticatedUser,
	selectUserGroups,
} from "../../../authentication/auth.selector";
import { useAppDispatch } from "../../../../common/hooks/default";
import { useContext, useMemo } from "react";
import * as _ from "lodash";
import { TenderSectionContext } from "./TenderSection";
import { TenderStatusCell } from "./TenderStatusCell";

interface TenderInformationTableProps {
	model: Tender[];
}

const style: any = {
	pickupMe: {
		opacity: 0,
		"&:hover": {
			opacity: 1,
			border: "1px dashed rgba(0,0,0,0.2)",
			padding: "10px",
			textAlign: "center",
			cursor: "pointer",
		},
	},
};

const statusCell = (m: Tender) => <TenderStatusCell tender={m} />;

export default function TenderInformationTable({
	model,
}: TenderInformationTableProps) {
	const dispatch = useAppDispatch();
	const currentUser = useSelector(selectAuthenticatedUser);
	const isUserTrader = useSelector(selectUserGroups)?.isTrader;
	const tenderSectionContext = useContext(TenderSectionContext);

	const rowModel: ColModel<Tender>[] = [
		{
			id: "status",
			header: () => "Status",
			xs: 2,
			noTooltip: true,
			accessor: statusCell,
		},
		{
			id: "name",
			header: () => "Name",
			xs: 2.5,
			accessor: (m: Tender) => m.name,
		},
		{
			id: "account",
			header: () => "Account",
			xs: 2.5,
			accessor: (m: Tender) => m.account.name,
		},
		{
			id: "country",
			header: () => "Country",
			xs: 1,
			accessor: (m: Tender) => (
				<>
					<ReactCountryFlag
						countryCode={m.countries.length ? m.countries[0] : ""}
						svg
						title={m.countries.length ? m.countries[0] : ""}
					/>{" "}
					{m.countries.length ? m.countries[0] : ""}
				</>
			),
		},
		{
			id: "currency",
			header: () => "Currency",
			xs: 1,
			accessor: (m: Tender) => <>{m.currency}</>,
		},
		{
			id: "capacity",
			header: () => "Capacity",
			xs: 1,
			accessor: (m: Tender) => m.installed_capacity_mw,
		},
		{
			id: "sites",
			header: () => "Sites",
			xs: 0.5,
			accessor: (m: Tender) =>
				`${m.count_attached_sites}/${m.site_count}`,
		},
		{
			id: "technology",
			header: () => "Techno",
			xs: 1.2,
			accessor: (m: Tender) =>
				m?.technologies?.length > 1 ? "Mixed" : m?.technologies,
		},
		{
			id: "ifrs_accounting",
			header: () => "Ifrs Accounting",
			xs: 1.2,
			accessor: (m: Tender) =>
				m?.ifrs_accounting
		},
		{
			id: "driection",
			header: () => "Direction",
			xs: 0.7,
			accessor: (m: Tender) => m.direction,
		},
		
		{
			id: "stage",
			header: () => "Pricing Stage",
			xs: 0.7,
			accessor: (m: Tender) => m.pricing_stage,
		},
		{
			id: "originator",
			header: () => "Originator",
			xs: 1.3,
			accessor: (m: Tender) => (
				<If condition={m?.originator}>
					<Typography variant="subtitle2" component="p">
						{m?.originator?.first_name} {m?.originator?.last_name}
					</Typography>
				</If>
			),
		},
		{
			id: "trader",
			header: () => "Trader",
			xs: 1.3,
			accessor: (m: Tender) => {
				const pickupAction = useMemo(
					() =>
						_.find(
							m.actions_blotter,
							(action) => action.action === "PICKUP"
						),
					[m]
				);
				return (
					<>
						<If condition={m?.trader}>
							<Typography variant="subtitle2" component="p">
								{m?.trader?.first_name} {m?.trader?.last_name}
							</Typography>
						</If>
						<If
							condition={
								!m?.trader && isUserTrader && pickupAction
							}
						>
							<Typography
								variant="subtitle2"
								component="p"
								sx={style.pickupMe}
								onClick={(e) => {
									e.preventDefault();
									e.stopPropagation();
									dispatch(
										updateTenders(
											[m.id],
											pickupAction!,
											currentUser?.name
										)
									);
								}}
							>
								Pickup
							</Typography>
						</If>
					</>
				);
			},
		},
		{
			id: "deadline",
			header: () => "Deadline",
			xs: 1,
			accessor: (m: Tender) => m.due_date,
		},
		{
			id: "validity",
			header: () => "Validity",
			xs: 1,
			accessor: (m: Tender) => m.validity_date,
		},
		{
			id: "history",
			header: () => "History",
			xs: 0.5,
			accessor: (m: Tender) => (
				<IconButton
					data-testid="tender-comments-icon"
					onClick={() => {
						tenderSectionContext?.setTenderActivityModal({
							tenderId: m.id,
							tenderName: m.name,
						});
					}}
				>
					<CommentIcon sx={{ fontSize: 16, color: primaryColor }} />
				</IconButton>
			),
		},
	];

	return (
		<GenericTable
			virtualScrollable={false}
			itemName="Sites"
			model={model}
			selection={() => {}}
			updatePage={() => {}}
			page={0}
			rowModel={rowModel}
			isSelectable={false}
			loading={false}
		/>
	);
}
